import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Wallet, PiggyBank, Landmark, Users, Store, Boxes, Receipt, Download,
} from 'lucide-react';
import { useCompany } from '../common/useCompany';
import generateFinancialPDF from '../../utils/generateFinancialPDF';
import { fmtMoney0, makeMoneyFormatter } from '../../utils/currency';

const fmt = fmtMoney0;

/** "USD 14,613 = AFN 959,103 · 11 bills" style dual-currency KPI text. */
function dualValue(byCurrencies, baseTotal, countLabel) {
  const list = (byCurrencies || []).filter(c => parseFloat(c.amount) > 0);
  if (list.length === 0) return { value: fmt(baseTotal), sub: countLabel };
  const parts = list.map(c => makeMoneyFormatter(String(c.currency), { currencyDisplay: 'code', minimumFractionDigits: 0, maximumFractionDigits: 0 })(parseFloat(c.amount)));
  return { value: parts.join(' + '), sub: `= ${fmt(baseTotal)} · ${countLabel}` };
}

function KpiCard({ icon: Icon, label, value, sub, path, tone }) {
  const navigate = useNavigate();
  return (
    <button type="button" className={`fin-kpi fin-kpi-${tone || ''}`} onClick={() => path && navigate(path)} title={path ? `Open ${label}` : label}>
      <span className="fin-kpi-icon"><Icon /></span>
      <span className="fin-kpi-text">
        <span className="fin-kpi-label">{label}</span>
        <span className="fin-kpi-value">{value}</span>
        {sub && <span className="fin-kpi-sub">{sub}</span>}
      </span>
    </button>
  );
}

/**
 * Financial Overview: compact KPI strip using real data from the
 * dashboard endpoint. Sits below the workflow map in the main column
 * of the desktop layout.
 */
export default function FinancialOverview({ kpi, balances, salesTrend, expenseTrend, topCustomers, topProducts, recentTransactions }) {
  const company = useCompany();
  if (!kpi) return null;

  const net = kpi.net_profit ?? 0;
  const AR = dualValue(kpi.accounts_receivable_by_currency, kpi.accounts_receivable, `${kpi.outstanding_invoices || 0} invoices`);
  const AP = dualValue(kpi.accounts_payable_by_currency, kpi.accounts_payable, `${kpi.outstanding_bills || 0} bills`);

  const handleExportPDF = () => {
    generateFinancialPDF({
      kpi, balances, salesTrend, expenseTrend, topCustomers, topProducts, recentTransactions,
      companyName: company?.name || 'LedgerPro',
    });
  };
  const kpis = [
    { icon: TrendingUp, label: 'Revenue (YTD)', value: fmt(kpi.revenue?.year_to_date), sub: `This month: ${fmt(kpi.revenue?.this_month)}`, path: '/invoices', tone: 'blue' },
    { icon: PiggyBank, label: 'Gross Profit', value: fmt(kpi.gross_profit), path: '/reports', tone: 'green' },
    { icon: Wallet, label: 'Net Profit', value: fmt(net), sub: net >= 0 ? 'Profitable' : 'Loss', path: '/reports', tone: net >= 0 ? 'green' : 'red' },
    { icon: Landmark, label: 'Cash & Bank', value: fmt(kpi.cash), path: '/banking', tone: 'navy' },
    { icon: Users, label: 'Accounts Receivable', value: AR.value, sub: AR.sub, path: '/invoices', tone: 'amber' },
    { icon: Store, label: 'Accounts Payable', value: AP.value, sub: AP.sub, path: '/bills', tone: 'red' },
    { icon: Boxes, label: 'Inventory Value', value: fmt(kpi.inventory_value), path: '/inventory', tone: 'teal' },
    { icon: Receipt, label: 'Expenses (YTD)', value: fmt(kpi.expenses?.year_to_date), sub: `This month: ${fmt(kpi.expenses?.this_month)}`, path: '/expenses', tone: 'slate' },
  ];

  return (
    <div className="fin-section">
      <div className="fin-section-header">
        <span className="fin-section-title">Financial Overview</span>
        <button type="button" className="fin-export-btn" onClick={handleExportPDF} title="Export Financial Overview as PDF">
          <Download size={13} /> Export PDF
        </button>
      </div>
      <div className="fin-kpis">
        {kpis.map(k => <KpiCard key={k.label} {...k} />)}
      </div>
    </div>
  );
}
